import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import axiosServices from '../../utils/axios';

const initialState = {
    searchResults: [],
    dealerData: null,
    loading: false,
    dealerLoading: false,
    error: null,
    dealerError: null,
    searchQuery: '',
    notifications: [],
    notificationLoading: false,
    notificationError: null,
    reportStats: [],
    reportStatsLoading: false,
    reportStatsError: null,
    previousServices: [],
    previousServicesLoading: false,
    previousServicesError: null,
    services: [],
    servicesLoading: false,
    servicesError: null,
    serviceTypes: [],
    serviceTypesLoading: false,
    serviceTypesError: null,
    serviceStats: [],
    serviceStatsLoading: false,
    serviceStatsError: null,
    nearbyIndustries: [],
    nearbyIndustriesLoading: false,
    nearbyIndustriesError: null,
    currentNotification: null,
    currentContract: null,
    locationUpdateLoading: false,
    locationUpdateError: null,
    serviceLimit: null,
    serviceLimitLoading: false,
    serviceLimitError: null,
    notificationAddLoading: false,
    notificationAddError: null,
    addedNotification: null,
    notification: null,
    packageInfo: null,
    packageInfoLoading: false,
    packageInfoError: null,
    notificationServiceLoading: false,
    notificationServiceError: null,
    notificationServiceValidationErrors: null,
    googleMapUrl: null,
    googleMapLoading: false,
    googleMapError: null,
    contractNoteLoading: false,
    contractNoteError: null,
    dealerSalesStats: [],
    dealerSalesStatsLoading: false,
    dealerSalesStatsError: null,
    saleNoteLoading: false,
    saleNoteError: null,
    saleNote: null,
    notificationServiceDetail: null,
    notificationServiceDetailLoading: false,
    notificationServiceDetailError: null,
    updateNotificationServiceLoading: false,
    updateNotificationServiceError: null,
    updateNotificationLoading: false,
    updateNotificationError: null,
    sendPaymentSmsLoading: false,
    sendPaymentSmsError: null,
    priceInfo: null,
    priceInfoLoading: false,
    priceInfoError: null,
    rejectNotificationServiceLoading: false,
    rejectNotificationServiceError: null,
    // CheckMate states
    checkMateServices: [],
    checkMateServicesLoading: false,
    checkMateServicesError: null,
    checkMateAddServiceLoading: false,
    checkMateAddServiceError: null,
    checkMateToken: null,
    checkMateTokenLoading: false,
    checkMateTokenError: null,
};

export const searchSales = createAsyncThunk(
    'callCenter/searchSales',
    async (dsSearch, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/CallCenter/sale-search`,
                { dsSearch }
            );

            if (response.data?.statu === 200) {
                return Array.isArray(response.data.data) ? response.data.data : [response.data.data];
            }

            return rejectWithValue(response.data?.exceptions?.[0] || 'Arama sonuçları alınamadı');
        } catch (error) {
            console.error("Arama hatası:", error);
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'Arama işlemi başarısız oldu');
        }
    }
);

export const fetchDealerCard = createAsyncThunk(
    'callCenter/fetchDealerCard',
    async (idDealer, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/CallCenter/dealer-card?idDealer=${idDealer}`,
            );

            if (response.data?.statu === 200) {
                return response.data.data[0];
            }

            return rejectWithValue(response.data?.exceptions?.[0] || 'Bayi bilgileri alınamadı');
        } catch (error) {
            console.error("Bayi kartı hatası:", error);
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'Bayi bilgileri yüklenirken hata oluştu');
        }
    }
);

export const searchNotifications = createAsyncThunk(
    'callCenter/searchNotifications',
    async (params, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/CallCenter/search-notification`,
                params
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }

            return rejectWithValue(response.data?.exceptions?.[0] || 'Bildirim sonuçları alınamadı');
        } catch (error) {
            console.error("Bildirim arama hatası:", error);
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'Bildirim arama işlemi başarısız oldu');
        }
    }
);

export const fetchReportStats = createAsyncThunk(
    'callCenter/fetchReportStats',
    async (params, { rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams();
            if (params?.startDate) queryParams.append('startDate', params.startDate);
            if (params?.endDate) queryParams.append('endDate', params.endDate);

            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/CallCenter/get-report-count?${queryParams}`
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }

            return rejectWithValue(response.data?.exceptions?.[0] || 'İstatistik bilgileri alınamadı');
        } catch (error) {
            console.error("İstatistik hatası:", error);
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'İstatistik bilgileri yüklenirken hata oluştu');
        }
    }
);

export const fetchPreviousServices = createAsyncThunk(
    'callCenter/fetchPreviousServices',
    async ({ dsContractNo }, { rejectWithValue }) => {
        try {
            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/CallCenter/get-previous-service?dsContractNo=${dsContractNo}`
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }

            return rejectWithValue(response.data?.exceptions?.[0] || 'Önceki hizmet bilgileri alınamadı');
        } catch (error) {
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'Önceki hizmet bilgileri yüklenirken hata oluştu');
        }
    }
);

export const fetchServices = createAsyncThunk(
    'callCenter/fetchServices',
    async (dsContractNo, { rejectWithValue }) => {
        try {
            // console.log('Fetching services for contract:', dsContractNo);
            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/CallCenter/get-services-contract?dsContractNo=${dsContractNo}`
            ); 
            // console.log('response', response);
            if (response.data?.statu === 200) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'Servis listesi alınamadı');
        } catch (error) {
            console.error('Services API error:', error);
            return rejectWithValue(error.message);
        }
    }
);

export const fetchServiceTypes = createAsyncThunk(
    'callCenter/fetchServiceTypes',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/CallCenter/get-service-type`
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'Hizmet türleri alınamadı');
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchServiceStats = createAsyncThunk(
    'callCenter/fetchServiceStats',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/CallCenter/get-all-services`
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'İstatistik bilgileri alınamadı');
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const addNotification = createAsyncThunk(
    'callCenter/addNotification',
    async (notificationData, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/CallCenter/add-ccn-notification`,
                notificationData
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            
            // API'den hata response'u geldi (statu !== 200)
            // Tüm hata durumlarını kontrol et: 401, 403, 500, 600, 700, 800
            if (response.data?.statu !== 200 && response.data?.exceptions && response.data.exceptions.length > 0) {
                return rejectWithValue(response.data);
            }
            
            // Exceptions yoksa genel hata mesajı
            return rejectWithValue({
                statu: response.data?.statu || 500,
                exceptions: response.data?.exceptions || [{ Message: 'İhbar kaydı oluşturulamadı' }]
            });
        } catch (error) { 
            // Axios interceptor'ı error.response.data'yı döndürüyor, bu yüzden error direkt olarak response.data olabilir
            // Önce error'un direkt response.data olup olmadığını kontrol et
            if (error?.statu && error?.exceptions) {
                // error zaten response.data formatında
                return rejectWithValue(error);
            }
            
            // Eğer error.response?.data varsa (normal axios error formatı)
            if (error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            
            // Eğer error bir string ise
            if (typeof error === 'string') {
                return rejectWithValue({ 
                    statu: 500,
                    exceptions: [{ message: error }]
                });
            }
            
            // Varsayılan hata mesajı
            return rejectWithValue({ 
                statu: 500,
                exceptions: [{ message: error?.message || 'İhbar kaydı oluşturulamadı' }]
            });
        }
    }
);

export const fetchNearbyIndustries = createAsyncThunk(
    'callCenter/fetchNearbyIndustries',
    async ({ latitude, longitude, brand, mtType }, { rejectWithValue }) => {
        try {
            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/CallCenter/get-industry-near?latitude=${latitude}&longitude=${longitude}&dsBrand=${brand}&mtType=${mtType}`,
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'Sanayi bilgileri alınamadı');
        } catch (error) {
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'Sanayi bilgileri yüklenirken hata oluştu');
        }
    }
);

export const updateLocation = createAsyncThunk(
    'callCenter/updateLocation',
    async ({idNotification, dsLatitude, dsLongitude }, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/Notification/update-location?idNotification=${idNotification}&location=${dsLatitude},${dsLongitude}` 
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'Konum güncellenemedi');
        } catch (error) {
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'Konum güncelleme hatası');
        }
    }
);

export const fetchServiceLimit = createAsyncThunk(
    'callCenter/fetchServiceLimit',
    async ({ dsContractNo, idPackage }, { rejectWithValue }) => {
        try {
            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/CallCenter/get-service-limit`,
                { params: { dsContractNo, idPackage } }
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'Limit bilgisi alınamadı');
        } catch (error) {
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'Limit bilgisi yüklenirken hata oluştu');
        }
    }
);

export const fetchNotification = createAsyncThunk(
    'callCenter/fetchNotification',
    async (idNotification, { rejectWithValue }) => {
        try {
            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/Notification/get-notification?idNotification=${idNotification}`
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'İhbar bilgileri alınamadı');
        } catch (error) {
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'İhbar bilgileri yüklenirken hata oluştu');
        }
    }
);

export const fetchPackageInfo = createAsyncThunk(
    'callCenter/fetchPackageInfo',
    async (dsContractNo, { rejectWithValue }) => {
        try {
            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/CallCenter/get-package-info`,
                { params: { dsContractNo } }
            );

            if (response.data?.statu === 200) {
                return response.data;
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'Paket bilgisi alınamadı');
        } catch (error) {
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'Paket bilgisi yüklenirken hata oluştu');
        }
    }
);
 

export const fetchGoogleMapUrl = createAsyncThunk(
    'callCenter/fetchGoogleMapUrl',
    async (idNotification, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/Global/get-google-map?idNotification=${idNotification}`,
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }

            return rejectWithValue(response.data?.exceptions?.[0] || 'Konum bilgisi alınamadı');
        } catch (error) {
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'Konum bilgisi yüklenirken hata oluştu');
        }
    }
);

export const updatePhoneNumber = createAsyncThunk(
    'callCenter/updatePhoneNumber',
    async ({ idNotification, dsCustomerPhone }, { rejectWithValue }) => {
        try {
            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/Notification/update-phone-number`,
                { params: { idNotification, dsCustomerPhone } }
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'Telefon numarası güncellenemedi');
        } catch (error) {
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'Telefon güncelleme hatası');
        }
    }
);

export const updateContractNote = createAsyncThunk(
    'callCenter/updateContractNote',
    async ({ dsContractNo, dsNote }, { rejectWithValue }) => {
        try {
            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/Notification/set-sales-note`,
                { 
                    params: { 
                        dsContractNo: dsContractNo, 
                        note: dsNote 
                    } 
                }
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'Not güncellenemedi');
        } catch (error) {
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'Not güncelleme hatası');
        }
    }
);

// Dealer satış istatistikleri için async thunk
export const fetchDealerSalesStats = createAsyncThunk(
    'callCenter/fetchDealerSalesStats',
    async ({ year, idDealer }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/CallCenter/dealer-sales-year?year=${year}&idDealer=${idDealer}`,
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }
);

// Bayi notu ekleme için async thunk
export const addDealerNote = createAsyncThunk(
    'callCenter/addDealerNote',
    async (noteData, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/App/add-callcenter-notes`,
                noteData
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'Not eklenemedi');
        } catch (error) {
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'Not ekleme hatası');
        }
    }
);

// Satış notu getirme için async thunk
export const fetchSaleNote = createAsyncThunk(
    'callCenter/fetchSaleNote',
    async (dsContractNo, { rejectWithValue }) => {
        try {
            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/App/get-sale?dsContractNo=${dsContractNo}`,
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'Not bilgisi alınamadı');
        } catch (error) {
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'Not bilgisi yüklenirken hata oluştu');
        }
    }
);

// Traffic Policy Upload için async thunk
export const uploadTrafficPolicy = createAsyncThunk(
    'callCenter/uploadTrafficPolicy',
    async ({ idFtpFile, dsContractNo }, { rejectWithValue }) => {
        try {
            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/Notification/upload-traffic-policy?idFtpFile=${idFtpFile}&dsContractNo=${dsContractNo}`
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'Dosya kaydedilemedi');
        } catch (error) {
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'Dosya yükleme hatası');
        }
    }
);

export const getNotificationService = createAsyncThunk(
    'callCenter/getNotificationService',
    async (idNotificationService, { rejectWithValue }) => {
        try {
            const response = await axiosServices.get(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/Notification/get-notification-service`,
                { params: { idNotificationService } }
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            
            // API'den hata response'u geldi (statu !== 200)
            if (response.data?.statu === 500 && response.data?.exceptions && response.data.exceptions.length > 0) {
                return rejectWithValue(response.data);
            }
            
            return rejectWithValue(response.data?.exceptions?.[0] || 'Bildirim servisi bilgileri alınamadı');
        } catch (error) {
            // Network hatası veya diğer hatalar
            if (error.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue({ message: error.message || 'Bildirim servisi bilgileri yüklenirken hata oluştu' });
        }
    }
);

export const updateNotificationService = createAsyncThunk(
    'callCenter/updateNotificationService',
    async (updateData, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/Notification/update-notification-service`,
                updateData
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            
            // API'den hata response'u geldi (statu !== 200)
            if (response.data?.statu === 500 && response.data?.exceptions && response.data.exceptions.length > 0) {
                return rejectWithValue(response.data);
            }
            
            return rejectWithValue(response.data?.exceptions?.[0] || 'Bildirim servisi güncellenemedi');
        } catch (error) { 
            // Network hatası veya diğer hatalar
            if (error.response?.data) {
                return rejectWithValue(error.exceptions[0].Message);
            }
            return rejectWithValue({ message:  error.exceptions[0].Message || 'Bildirim servisi güncelleme hatası' });
        }
    }
);

export const updateNotification = createAsyncThunk(
    'callCenter/updateNotification',
    async (updateData, { rejectWithValue }) => {
        try {
            // UpdateCcnNotificationRequest: tüm alanlar backend'e gönderilir (eksikler default)
            const body = {
                idNotification: updateData.idNotification ?? 0,
                idCity: updateData.idCity ?? 0,
                idDistrict: updateData.idDistrict ?? 0,
                flPayment: updateData.flPayment ?? false,
                mtPayment: updateData.mtPayment ?? 0,
                mtBidPrice: updateData.mtBidPrice ?? updateData.MtBidPrice ?? 0,
                dsLatitudeEnd: formatCoordForApi(updateData.dsLatitudeEnd) ?? null,
                dsLongitudeEnd: formatCoordForApi(updateData.dsLongitudeEnd) ?? null,
            };

            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/Notification/update-notification`,
                body,
                { headers: { 'Content-Type': 'application/json' } }
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            if (response.data?.statu === 500 && response.data?.exceptions && response.data.exceptions.length > 0) {
                return rejectWithValue(response.data);
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'Bildirim güncellenemedi');
        } catch (error) {
            if (error.response?.data) {
                return rejectWithValue(error.response.data?.exceptions?.[0] || error.response.data);
            }
            return rejectWithValue({ message: error.message || 'Bildirim güncelleme hatası' });
        }
    }
);

/** SendPaymentSmsRequest — ücretli çekimde müşteriye ödeme SMS (idCompany ≠ 60 senaryosu) */
export const sendPaymentSms = createAsyncThunk(
    'callCenter/sendPaymentSms',
    async (payload, { rejectWithValue }) => {
        try {
            const body = {
                idNotification: Number(payload.idNotification) || 0,
                mtPayment: Number(payload.mtPayment) || 0,
                dsContractNo: payload.dsContractNo != null ? String(payload.dsContractNo) : null,
                dsPhone: payload.dsPhone != null ? String(payload.dsPhone) : null,
            };
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/App/send-payment-sms`,
                body,
                { headers: { 'Content-Type': 'application/json' } }
            );
            if (response.data?.statu === 200) {
                return response.data.data;
            }
            if (response.data?.statu === 500 && response.data?.exceptions?.length) {
                return rejectWithValue(response.data);
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'Ödeme SMS gönderilemedi');
        } catch (error) {
            if (error.response?.data) {
                return rejectWithValue(error.response.data?.exceptions?.[0] || error.response.data);
            }
            return rejectWithValue({ message: error.message || 'Ödeme SMS gönderilirken hata oluştu' });
        }
    }
);

/** Konum değerini endpoint'in beklediği string formata çevirir: ondalık ayırıcı nokta (.), geçerli enlem/boylam */
const formatCoordForApi = (v) => {
    if (v == null || v === '') return undefined;
    const s = typeof v === 'string' ? v.trim().replace(',', '.') : String(v);
    const num = parseFloat(s);
    if (!Number.isFinite(num)) return undefined;
    const formatted = num.toFixed(8).replace(/\.?0+$/, '');
    return formatted === '' ? String(num) : formatted;
};

/** idVehicleType → yol yardım API'nin beklediği iki haneli kod (örn. 1 → "01") */
const formatVehicleTypeCodeForYolYardim = (idVehicleType) => {
    const n = Number(idVehicleType);
    const safe = Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
    return String(safe).padStart(2, '0');
};

/**
 * get-price-info / Price/get-price cevabında mesafe genelde metre, süre saniye olabiliyor;
 * UI ve mtKm alanı km + okunabilir süre (dk) bekler.
 * 50'den büyük mesafe değerlerini m→km kabul eder (örn. 10917 → 10,9 km).
 */
const normalizePriceInfoDistanceDuration = (data) => {
    if (!data || typeof data !== 'object') return data;
    const out = { ...data };
    const rawDist = out.mtkm ?? out.mtKm;
    if (rawDist != null && Number(rawDist) > 50) {
        const km = Number(rawDist) / 1000;
        out.mtkm = km;
        out.mtKm = km;
    }
    if (out.dsDuration != null) {
        const s = String(out.dsDuration).trim();
        const num = parseFloat(s.replace(/\s/g, '').replace(',', '.'));
        if (Number.isFinite(num) && num > 120 && !/\b(dk|min|dak|saat|sn|sec|hour|h)\b/i.test(s)) {
            out.dsDuration = `${Math.round(num / 60)} dk`;
        }
    }
    return out;
};

/** Yol yardım Price/get-price cevabını get-price-info ile aynı alanlarda kullanılabilir hale getirir */
const normalizeYolYardimPriceData = (data) => {
    if (!data || typeof data !== 'object') return data;
    const mtPrice = data.mtPrice;
    const dist = data.mtDistance ?? data.mtKm ?? data.mtkm;
    const bid = data.mtBidPrice ?? data.MtBidPrice ?? mtPrice;
    const merged = {
        mtPrice,
        mtkm: dist,
        mtKm: dist,
        mtBidPrice: bid,
        MtBidPrice: bid,
        dsDuration:
            data.dsDuration != null
                ? String(data.dsDuration)
                : data.mtDuration != null
                  ? String(data.mtDuration)
                  : undefined,
    };
    return normalizePriceInfoDistanceDuration(merged);
};

export const getPriceInfo = createAsyncThunk(
    'callCenter/getPriceInfo',
    async (params, { rejectWithValue, getState }) => {
        try {
            const idCompany = Number(params.idCompany);
            const useCallCenterPrice = idCompany === 60;

            const bodyCallCenter = {
                idCompany: idCompany || 0,
                idCity: Number(params.idCity) || 0,
                idDistrict: params.idDistrict != null ? Number(params.idDistrict) : undefined,
                dsLatitude: formatCoordForApi(params.dsLatitude),
                dsLongitude: formatCoordForApi(params.dsLongitude),
                dsLatitudeEnd: formatCoordForApi(params.dsLatitudeEnd),
                dsLongitudeEnd: formatCoordForApi(params.dsLongitudeEnd),
                dsVehicleState: params.dsVehicleState,
                idVehicleType: params.idVehicleType ?? 1,
            };

            if (useCallCenterPrice) {
                const response = await axiosServices.post(
                    `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/Notification/get-price-info`,
                    bodyCallCenter
                );
                if (response.data?.statu === 200) {
                    return normalizePriceInfoDistanceDuration(response.data.data);
                }
                return rejectWithValue(response.data?.exceptions?.[0] || 'Fiyat bilgisi alınamadı');
            }

            const idVt = params.idVehicleType ?? 1;
            // dsVehicleType: API iki haneli kod bekliyor (örn. "01"); açıkça verilmişse onu kullan
            const dsVehicleType =
                params.dsVehicleType != null && String(params.dsVehicleType).trim() !== ''
                    ? String(params.dsVehicleType).trim()
                    : formatVehicleTypeCodeForYolYardim(idVt);

            const bodyYolYardim = {
                idCity: Number(params.idCity) || 0,
                dsLatitude: formatCoordForApi(params.dsLatitude),
                dsLongitude: formatCoordForApi(params.dsLongitude),
                dsLatitudeEnd: formatCoordForApi(params.dsLatitudeEnd),
                dsLongitudeEnd: formatCoordForApi(params.dsLongitudeEnd),
                dsVehicleState: params.dsVehicleState != null ? String(params.dsVehicleState) : '',
                dsVehicleType,
            };

            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/yolyardim-service/api/Price/get-price`,
                bodyYolYardim,
                { headers: { 'Content-Type': 'application/json' } }
            );
            if (response.data?.statu === 200) {
                return normalizeYolYardimPriceData(response.data.data);
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'Fiyat bilgisi alınamadı');
        } catch (error) {
            if (error.response?.data) {
                return rejectWithValue(error.response.data?.exceptions?.[0] || error.response.data);
            }
            return rejectWithValue({ message: error.message || 'Fiyat bilgisi yüklenirken hata oluştu' });
        }
    }
);

export const rejectNotificationService = createAsyncThunk(
    'callCenter/rejectNotificationService',
    async (idNotificationService, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/Supplier/delete-notification-vehicle?idNotificationService=${idNotificationService}`,
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'Bildirim servisi reddedilemedi');
        } catch (error) {
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'Bildirim servisi reddetme hatası');
        }
    }
);

// CheckMate API functions
export const fetchCheckMateServices = createAsyncThunk(
    'callCenter/fetchCheckMateServices',
    async (mtState, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/yolyardim-service/api/CheckMate/get-service?mtState=${mtState || '0'}`,
            );

            if (response.data?.statu === 200) {
                return response.data.data || [];
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'CheckMate servisleri alınamadı');
        } catch (error) {
            console.error("CheckMate servisleri hatası:", error);
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'CheckMate servisleri yüklenirken hata oluştu');
        }
    }
);

export const addCheckMateService = createAsyncThunk(
    'callCenter/addCheckMateService',
    async (serviceData, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/yolyardim-service/api/CheckMate/add-service`,
                serviceData
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.exceptions?.[0] || 'CheckMate servisi eklenemedi');
        } catch (error) {
            console.error("CheckMate servis ekleme hatası:", error);
            return rejectWithValue(error.response?.data?.exceptions?.[0] || 'CheckMate servisi eklenirken hata oluştu');
        }
    }
);

export const updateCheckMateService = createAsyncThunk(
    'callCenter/updateCheckMateService',
    async (serviceData, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/yolyardim-service/api/CheckMate/update-service`,
                serviceData
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            
            // API'den hata response'u geldi (statu !== 200)
            if (response.data?.statu !== 200 && response.data?.exceptions && response.data.exceptions.length > 0) {
                return rejectWithValue(response.data);
            }
            
            return rejectWithValue({
                statu: response.data?.statu || 500,
                exceptions: response.data?.exceptions || [{ message: 'CheckMate servisi güncellenemedi' }]
            });
        } catch (error) {
            // Axios interceptor'ı error.response.data'yı döndürüyor
            if (error?.statu && error?.exceptions) {
                return rejectWithValue(error);
            }
            
            if (error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            
            return rejectWithValue({ 
                statu: 500,
                exceptions: [{ message: error?.message || 'CheckMate servisi güncellenirken hata oluştu' }]
            });
        }
    }
);

export const getCheckMateToken = createAsyncThunk(
    'callCenter/getCheckMateToken',
    async (tokenData, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/yolyardim-service/api/CheckMate/get-token`,
                tokenData
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }
            
            return rejectWithValue(response.data?.exceptions?.[0] || 'Token alınamadı');
        } catch (error) {
            if (error?.statu && error?.exceptions) {
                return rejectWithValue(error);
            }
            
            if (error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            
            return rejectWithValue({ 
                statu: 500,
                exceptions: [{ message: error?.message || 'Token alınırken hata oluştu' }]
            });
        }
    }
);

// Memoized selectors
export const selectCurrentContract = createSelector(
    state => state.callCenter,
    callCenter => callCenter.currentContract
);

export const selectCurrentNotification = createSelector(
    state => state.callCenter,
    callCenter => callCenter.currentNotification
);

export const selectNotificationAddError = createSelector(
    state => state.callCenter,
    callCenter => callCenter.notificationAddError
);

export const callCenterSlice = createSlice({
    name: 'callCenter',
    initialState,
    reducers: {
        clearSearchData: (state) => {
            state.searchResults = [];
            state.dealerData = null;
            state.error = null;
            state.dealerError = null;
        },
        clearSearchResults: (state) => {
            state.searchResults = [];
            state.error = null;
        },
        clearDealerData: (state) => {
            state.dealerData = null;
            state.dealerError = null;
        },
        setSearchQuery: (state, action) => {
            state.searchQuery = action.payload;
        },
        clearNotifications: (state) => {
            state.notifications = [];
            state.notificationError = null;
        },
        clearReportStats: (state) => {
            state.reportStats = [];
            state.reportStatsError = null;
        },
        setCurrentNotification: (state, action) => {
            state.currentNotification = action.payload;
        },
        setCurrentContract: (state, action) => {
            state.currentContract = action.payload;
        },
        clearNotification: (state) => {
            state.notification = null;
            state.notificationLoading = false;
            state.notificationError = null;
        },
        clearPackageInfo: (state) => {
            state.packageInfo = null;
            state.packageInfoError = null;
        },
        clearGoogleMapData: (state) => {
            state.googleMapUrl = null;
            state.googleMapError = null;
        },
        clearNotificationServiceDetail: (state) => {
            state.notificationServiceDetail = null;
            state.notificationServiceDetailError = null;
        },
        clearCheckMateServices: (state) => {
            state.checkMateServices = [];
            state.checkMateServicesError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(searchSales.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(searchSales.fulfilled, (state, action) => {
                state.loading = false;
                state.searchResults = action.payload;
                state.error = null;
            })
            .addCase(searchSales.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.searchResults = [];
            })
            .addCase(fetchDealerCard.pending, (state) => {
                state.dealerLoading = true;
                state.dealerError = null;
            })
            .addCase(fetchDealerCard.fulfilled, (state, action) => {
                state.dealerLoading = false;
                state.dealerData = action.payload;
                state.dealerError = null; 
            })
            .addCase(fetchDealerCard.rejected, (state, action) => {
                state.dealerLoading = false;
                state.dealerError = action.payload;
                state.dealerData = null;
            })
            .addCase(searchNotifications.pending, (state) => {
                state.notificationLoading = true;
                state.notificationError = null;
            })
            .addCase(searchNotifications.fulfilled, (state, action) => {
                state.notificationLoading = false;
                state.notifications = action.payload;
                state.notificationError = null;
            })
            .addCase(searchNotifications.rejected, (state, action) => {
                state.notificationLoading = false;
                state.notificationError = action.payload;
                state.notifications = [];
            })
            .addCase(fetchReportStats.pending, (state) => {
                state.reportStatsLoading = true;
                state.reportStatsError = null;
            })
            .addCase(fetchReportStats.fulfilled, (state, action) => {
                state.reportStatsLoading = false;
                state.reportStats = action.payload;
                state.reportStatsError = null;
            })
            .addCase(fetchReportStats.rejected, (state, action) => {
                state.reportStatsLoading = false;
                state.reportStatsError = action.payload;
                state.reportStats = [];
            })
            .addCase(fetchPreviousServices.pending, (state) => {
                state.previousServicesLoading = true;
                state.previousServicesError = null;
            })
            .addCase(fetchPreviousServices.fulfilled, (state, action) => {
                state.previousServicesLoading = false;
                state.previousServices = action.payload;
                state.previousServicesError = null;
            })
            .addCase(fetchPreviousServices.rejected, (state, action) => {
                state.previousServicesLoading = false;
                state.previousServicesError = action.payload;
                state.previousServices = [];
            })
            .addCase(fetchServices.pending, (state) => {
                state.servicesLoading = true;
                state.servicesError = null;
            })
            .addCase(fetchServices.fulfilled, (state, action) => {
                state.servicesLoading = false;
                state.services = action.payload;
                state.servicesError = null;
            })
            .addCase(fetchServices.rejected, (state, action) => {
                state.servicesLoading = false;
                state.servicesError = action.payload;
                state.services = [];
            })
            .addCase(fetchServiceTypes.pending, (state) => {
                state.serviceTypesLoading = true;
                state.serviceTypesError = null;
            })
            .addCase(fetchServiceTypes.fulfilled, (state, action) => {
                state.serviceTypesLoading = false;
                state.serviceTypes = action.payload;
                state.serviceTypesError = null;
            })
            .addCase(fetchServiceTypes.rejected, (state, action) => {
                state.serviceTypesLoading = false;
                state.serviceTypesError = action.payload;
                state.serviceTypes = [];
            })
            .addCase(fetchServiceStats.pending, (state) => {
                state.serviceStatsLoading = true;
                state.serviceStatsError = null;
            })
            .addCase(fetchServiceStats.fulfilled, (state, action) => {
                state.serviceStatsLoading = false;
                state.serviceStats = action.payload;
                state.serviceStatsError = null;
            })
            .addCase(fetchServiceStats.rejected, (state, action) => {
                state.serviceStatsLoading = false;
                state.serviceStatsError = action.payload;
                state.serviceStats = [];
            })
            .addCase(addNotification.pending, (state) => {
                state.notificationAddLoading = true;
                state.notificationAddError = null;
            })
            .addCase(addNotification.fulfilled, (state, action) => {
                state.notificationAddLoading = false;
                state.addedNotification = action.payload;
                state.notificationAddError = null;
            })
            .addCase(addNotification.rejected, (state, action) => {
                state.notificationAddLoading = false;
                state.notificationAddError = null;
                state.notificationAddError = action.payload;
            })
            .addCase(fetchNearbyIndustries.pending, (state) => {
                state.nearbyIndustriesLoading = true;
                state.nearbyIndustriesError = null;
            })
            .addCase(fetchNearbyIndustries.fulfilled, (state, action) => {
                state.nearbyIndustriesLoading = false;
                state.nearbyIndustries = action.payload;
            })
            .addCase(fetchNearbyIndustries.rejected, (state, action) => {
                state.nearbyIndustriesLoading = false;
                state.nearbyIndustriesError = action.payload;
                state.nearbyIndustries = [];
            })
            .addCase(updateLocation.pending, (state) => {
                state.locationUpdateLoading = true;
                state.locationUpdateError = null;
            })
            .addCase(updateLocation.fulfilled, (state, action) => {
                state.locationUpdateLoading = false;
                state.locationUpdateError = null;
                if (state.currentNotification) {
                    state.currentNotification = {
                        ...state.currentNotification,
                        ...action.payload
                    };
                }
            })
            .addCase(updateLocation.rejected, (state, action) => {
                state.locationUpdateLoading = false;
                state.locationUpdateError = action.payload;
            })
            .addCase(fetchServiceLimit.pending, (state) => {
                state.serviceLimitLoading = true;
                state.serviceLimitError = null;
            })
            .addCase(fetchServiceLimit.fulfilled, (state, action) => {
                state.serviceLimitLoading = false;
                state.serviceLimit = action.payload;
                state.serviceLimitError = null;
            })
            .addCase(fetchServiceLimit.rejected, (state, action) => {
                state.serviceLimitLoading = false;
                state.serviceLimitError = action.payload;
            })
            .addCase(fetchNotification.pending, (state) => {
                state.notificationLoading = true;
                state.notificationError = null;
            })
            .addCase(fetchNotification.fulfilled, (state, action) => {
                state.notificationLoading = false;
                state.notification = action.payload;
                state.notificationError = null;
            })
            .addCase(fetchNotification.rejected, (state, action) => {
                state.notificationLoading = false;
                state.notificationError = action.payload;
                state.notification = null;
            })
            .addCase(fetchPackageInfo.pending, (state) => {
                state.packageInfoLoading = true;
                state.packageInfoError = null;
            })
            .addCase(fetchPackageInfo.fulfilled, (state, action) => {
                state.packageInfoLoading = false;
                state.packageInfo = action.payload;
            })
            .addCase(fetchPackageInfo.rejected, (state, action) => {
                state.packageInfoLoading = false;
                state.packageInfoError = action.payload;
            }) 
            .addCase(fetchGoogleMapUrl.pending, (state) => {
                state.googleMapLoading = true;
                state.googleMapError = null;
            })
            .addCase(fetchGoogleMapUrl.fulfilled, (state, action) => {
                state.googleMapLoading = false;
                state.googleMapUrl = action.payload;
            })
            .addCase(fetchGoogleMapUrl.rejected, (state, action) => {
                state.googleMapLoading = false;
                state.googleMapError = action.payload;
            })
            .addCase(updatePhoneNumber.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updatePhoneNumber.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(updatePhoneNumber.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateContractNote.pending, (state) => {
                state.contractNoteLoading = true;
                state.contractNoteError = null;
            })
            .addCase(updateContractNote.fulfilled, (state) => {
                state.contractNoteLoading = false;
                state.contractNoteError = null;
            })
            .addCase(updateContractNote.rejected, (state, action) => {
                state.contractNoteLoading = false;
                state.contractNoteError = action.payload;
            })
            .addCase(fetchDealerSalesStats.pending, (state) => {
                state.dealerSalesStatsLoading = true;
                state.dealerSalesStatsError = null;
            })
            .addCase(fetchDealerSalesStats.fulfilled, (state, action) => {
                state.dealerSalesStatsLoading = false;
                state.dealerSalesStats = action.payload.data;
            })
            .addCase(fetchDealerSalesStats.rejected, (state, action) => {
                state.dealerSalesStatsLoading = false;
                state.dealerSalesStatsError = action.error.message;
            })
            .addCase(addDealerNote.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addDealerNote.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(addDealerNote.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchSaleNote.pending, (state) => {
                state.saleNoteLoading = true;
                state.saleNoteError = null;
            })
            .addCase(fetchSaleNote.fulfilled, (state, action) => {
                state.saleNoteLoading = false;
                state.saleNote = action.payload;
            })
            .addCase(fetchSaleNote.rejected, (state, action) => {
                state.saleNoteLoading = false;
                state.saleNoteError = action.payload;
            })
            .addCase(uploadTrafficPolicy.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
            })
            .addCase(uploadTrafficPolicy.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(getNotificationService.pending, (state) => {
                state.notificationServiceDetailLoading = true;
                state.notificationServiceDetailError = null;
            })
            .addCase(getNotificationService.fulfilled, (state, action) => {
                state.notificationServiceDetailLoading = false;
                state.notificationServiceDetail = action.payload;
                state.notificationServiceDetailError = null;
            })
            .addCase(getNotificationService.rejected, (state, action) => {
                state.notificationServiceDetailLoading = false;
                state.notificationServiceDetailError = action.payload;
                state.notificationServiceDetail = null;
            })
            .addCase(updateNotificationService.pending, (state) => {
                state.updateNotificationServiceLoading = true;
                state.updateNotificationServiceError = null;
            })
            .addCase(updateNotificationService.fulfilled, (state) => {
                state.updateNotificationServiceLoading = false;
                state.updateNotificationServiceError = null;
            })
            .addCase(updateNotificationService.rejected, (state, action) => {
                state.updateNotificationServiceLoading = false;
                state.updateNotificationServiceError = action.payload;
            })
            .addCase(updateNotification.pending, (state) => {
                state.updateNotificationLoading = true;
                state.updateNotificationError = null;
            })
            .addCase(updateNotification.fulfilled, (state) => {
                state.updateNotificationLoading = false;
                state.updateNotificationError = null;
            })
            .addCase(updateNotification.rejected, (state, action) => {
                state.updateNotificationLoading = false;
                state.updateNotificationError = action.payload;
            })
            .addCase(sendPaymentSms.pending, (state) => {
                state.sendPaymentSmsLoading = true;
                state.sendPaymentSmsError = null;
            })
            .addCase(sendPaymentSms.fulfilled, (state) => {
                state.sendPaymentSmsLoading = false;
                state.sendPaymentSmsError = null;
            })
            .addCase(sendPaymentSms.rejected, (state, action) => {
                state.sendPaymentSmsLoading = false;
                state.sendPaymentSmsError = action.payload;
            })
            .addCase(getPriceInfo.pending, (state) => {
                state.priceInfoLoading = true;
                state.priceInfoError = null;
                state.priceInfo = null;
            })
            .addCase(getPriceInfo.fulfilled, (state, action) => {
                state.priceInfoLoading = false;
                state.priceInfoError = null;
                state.priceInfo = action.payload;
            })
            .addCase(getPriceInfo.rejected, (state, action) => {
                state.priceInfoLoading = false;
                state.priceInfoError = action.payload;
                state.priceInfo = null;
            })
            .addCase(rejectNotificationService.pending, (state) => {
                state.rejectNotificationServiceLoading = true;
                state.rejectNotificationServiceError = null;
            })
            .addCase(rejectNotificationService.fulfilled, (state) => {
                state.rejectNotificationServiceLoading = false;
                state.rejectNotificationServiceError = null;
            })
            .addCase(rejectNotificationService.rejected, (state, action) => {
                state.rejectNotificationServiceLoading = false;
                state.rejectNotificationServiceError = action.payload;
            })
            .addCase(fetchCheckMateServices.pending, (state) => {
                state.checkMateServicesLoading = true;
                state.checkMateServicesError = null;
            })
            .addCase(fetchCheckMateServices.fulfilled, (state, action) => {
                state.checkMateServicesLoading = false;
                state.checkMateServices = action.payload;
                state.checkMateServicesError = null;
            })
            .addCase(fetchCheckMateServices.rejected, (state, action) => {
                state.checkMateServicesLoading = false;
                state.checkMateServicesError = action.payload;
                state.checkMateServices = [];
            })
            .addCase(addCheckMateService.pending, (state) => {
                state.checkMateAddServiceLoading = true;
                state.checkMateAddServiceError = null;
            })
            .addCase(addCheckMateService.fulfilled, (state, action) => {
                state.checkMateAddServiceLoading = false;
                state.checkMateAddServiceError = null;
            })
            .addCase(addCheckMateService.rejected, (state, action) => {
                state.checkMateAddServiceLoading = false;
                state.checkMateAddServiceError = action.payload;
            })
            .addCase(updateCheckMateService.pending, (state) => {
                state.checkMateAddServiceLoading = true;
                state.checkMateAddServiceError = null;
            })
            .addCase(updateCheckMateService.fulfilled, (state, action) => {
                state.checkMateAddServiceLoading = false;
                state.checkMateAddServiceError = null;
            })
            .addCase(updateCheckMateService.rejected, (state, action) => {
                state.checkMateAddServiceLoading = false;
                state.checkMateAddServiceError = action.payload;
            })
            .addCase(getCheckMateToken.pending, (state) => {
                state.checkMateTokenLoading = true;
                state.checkMateTokenError = null;
            })
            .addCase(getCheckMateToken.fulfilled, (state, action) => {
                state.checkMateTokenLoading = false;
                state.checkMateToken = action.payload;
                state.checkMateTokenError = null;
            })
            .addCase(getCheckMateToken.rejected, (state, action) => {
                state.checkMateTokenLoading = false;
                state.checkMateTokenError = action.payload;
                state.checkMateToken = null;
            });
    }
});

export const { 
    clearSearchData, 
    clearSearchResults, 
    clearDealerData, 
    setSearchQuery, 
    clearNotifications,
    clearReportStats,
    setCurrentNotification,
    setCurrentContract,
    clearNotification,
    clearPackageInfo,
    clearGoogleMapData,
    clearNotificationServiceDetail,
    clearCheckMateServices,
} = callCenterSlice.actions;

export default callCenterSlice.reducer;
